/**
 * Tab Appointments nell'Admin Panel.
 * Gestione appuntamenti con CRUD completo.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Pencil, CheckCircle } from 'lucide-react'
import { appointmentsApi, AppointmentDetail } from '../../api/appointments'
import { SkeletonList, SkeletonText } from '../../components/Skeleton'
import { Breadcrumb } from '@/components/Breadcrumb'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export function AppointmentsTab() {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [dateFilter, setDateFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [showEdit, setShowEdit] = useState(false)
  const [editForm, setEditForm] = useState({
    datetime_start: '',
    visit_type: '',
    notes: '',
    status: '',
  })

  const queryClient = useQueryClient()

  const { data: appointments, isLoading } = useQuery({
    queryKey: ['appointments-list', dateFilter, statusFilter],
    queryFn: () => appointmentsApi.list({
      date_from: dateFilter || undefined,
      date_to: dateFilter || undefined,
      status: statusFilter || undefined,
    }),
    enabled: !selectedId,
  })

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['appointment-detail', selectedId],
    queryFn: () => appointmentsApi.get(selectedId!),
    enabled: !!selectedId && !showEdit,
  })

  const deleteMutation = useMutation({
    mutationFn: appointmentsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments-list'] })
      setSelectedId(null)
    },
    onError: () => {
      alert('Errore durante la cancellazione')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof editForm }) =>
      appointmentsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments-list'] })
      queryClient.invalidateQueries({ queryKey: ['appointment-detail', selectedId] })
      setShowEdit(false)
    },
    onError: () => {
      alert('Errore durante l\'aggiornamento')
    },
  })

  const handleDelete = (id: string) => {
    if (confirm('Eliminare questo appuntamento?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleEdit = (appt: AppointmentDetail) => {
    setEditForm({
      datetime_start: appt.datetime_start.slice(0, 16),
      visit_type: appt.visit_type,
      notes: appt.notes || '',
      status: appt.status,
    })
    setShowEdit(true)
  }

  const handleSaveEdit = () => {
    if (!selectedId) return
    updateMutation.mutate({ id: selectedId, data: editForm })
  }

  // DETAIL VIEW
  if (detail && !showEdit) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <Breadcrumb items={[
            { label: 'Appuntamenti', href: '' },
            { label: detail.patient.name },
          ]} />
          <Button variant="ghost" size="sm" onClick={() => handleEdit(detail)} className="text-primary">
            <Pencil className="size-3.5" /> Modifica
          </Button>
        </div>

        {detailLoading ? (
          <SkeletonText lines={8} />
        ) : (
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">
                Appuntamento — {detail.patient.name}
              </h2>
              <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                <div>
                  <span className="text-muted-foreground">Paziente:</span>{' '}
                  <span className="font-medium">{detail.patient.name} ({detail.patient.species})</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Proprietario:</span>{' '}
                  <span className="font-medium">{detail.owner.full_name || detail.owner.email}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Veterinario:</span>{' '}
                  <span className="font-medium">{detail.vet?.full_name || '-'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Tipo:</span>{' '}
                  <span className="font-medium">{detail.visit_type || '-'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Data/Ora:</span>{' '}
                  <span className="font-medium">
                    {detail.datetime_start ? new Date(detail.datetime_start).toLocaleString('it-IT') : '-'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Fine:</span>{' '}
                  <span className="font-medium">
                    {detail.datetime_end ? new Date(detail.datetime_end).toLocaleString('it-IT') : '-'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Stato:</span>{' '}
                  <Badge variant="outline" className={
                    detail.status === 'confirmed' ? 'bg-success/10 text-success border-success/20' :
                    detail.status === 'cancelled' ? 'bg-danger/10 text-danger border-danger/20' :
                    'bg-muted text-muted-foreground border-border'
                  }>
                    {detail.status}
                  </Badge>
                </div>
                <div>
                  <span className="text-muted-foreground">Google Calendar:</span>{' '}
                  <span className="font-medium">
                    {detail.gcal_event_id ? <CheckCircle className="size-3.5 text-success inline" /> : '-'}
                  </span>
                </div>
              </div>
              {detail.notes && (
                <div className="pt-4 border-t border-border">
                  <span className="text-muted-foreground text-sm">Note:</span>
                  <p className="text-sm mt-1">{detail.notes}</p>
                </div>
              )}
              <div className="flex gap-2 mt-6">
                <Button variant="destructive" onClick={() => handleDelete(detail.id)} disabled={deleteMutation.isPending}>
                  {deleteMutation.isPending ? 'Cancellazione...' : 'Cancella'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    )
  }

  // EDIT VIEW
  if (showEdit && detail) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <Breadcrumb items={[
            { label: 'Appuntamenti', href: '' },
            { label: detail.patient.name, href: '' },
            { label: 'Modifica' },
          ]} />
        </div>

        <Card>
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">
              Modifica Appuntamento — {detail.patient.name}
            </h2>
            <div className="space-y-4">
              <div>
                <Label>Data/Ora Inizio</Label>
                <Input
                  type="datetime-local"
                  value={editForm.datetime_start}
                  onChange={(e) => setEditForm({ ...editForm, datetime_start: e.target.value })}
                />
              </div>
              <div>
                <Label>Tipo Visita</Label>
                <Input
                  value={editForm.visit_type}
                  onChange={(e) => setEditForm({ ...editForm, visit_type: e.target.value })}
                />
              </div>
              <div>
                <Label>Stato</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confermato</SelectItem>
                    <SelectItem value="cancelled">Cancellato</SelectItem>
                    <SelectItem value="pending">In attesa</SelectItem>
                    <SelectItem value="completed">Completato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveEdit} disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Salvataggio...' : 'Salva'}
                </Button>
                <Button variant="outline" onClick={() => setShowEdit(false)}>
                  Annulla
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // LIST VIEW
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Appuntamenti</h2>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="w-auto" />
        <Select value={statusFilter || undefined} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Tutti gli stati" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="confirmed">Confermato</SelectItem>
            <SelectItem value="cancelled">Cancellato</SelectItem>
            <SelectItem value="pending">In attesa</SelectItem>
            <SelectItem value="completed">Completato</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <SkeletonList count={5} />
      ) : appointments?.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground text-sm">
            {dateFilter || statusFilter ? 'Nessun risultato per i filtri' : 'Nessun appuntamento trovato'}
          </CardContent>
        </Card>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Paziente</TableHead>
                <TableHead>Veterinario</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appointments?.map((appt) => (
                <TableRow key={appt.id}>
                  <TableCell className="whitespace-nowrap">
                    {appt.datetime_start ? new Date(appt.datetime_start).toLocaleString('it-IT', {
                      day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                    }) : '-'}
                  </TableCell>
                  <TableCell className="font-medium">
                    {appt.patient_name || 'N/D'}
                  </TableCell>
                  <TableCell>
                    {appt.vet_name || '-'}
                  </TableCell>
                  <TableCell>{appt.visit_type || '-'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      appt.status === 'confirmed' ? 'bg-success/10 text-success border-success/20' :
                      appt.status === 'cancelled' ? 'bg-danger/10 text-danger border-danger/20' :
                      appt.status === 'completed' ? 'bg-info/10 text-info border-info/20' :
                      'bg-muted text-muted-foreground border-border'
                    }>
                      {appt.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" className="h-auto p-0" onClick={() => setSelectedId(appt.id)}>
                        Dettaglio
                      </Button>
                      <Button variant="ghost" size="sm" className="h-auto p-0 text-destructive" onClick={() => handleDelete(appt.id)}>
                        Elimina
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
